import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { BottomNavigation } from "@/components/layout/BottomNavigation";
import { SessionCard, GuidedTextPlayer, MEDITATION_CATEGORIES } from "@/components/MeditationSection";
import { ArrowLeft, Clock, Moon, Sparkles, Wind } from "lucide-react";

export default function MeditationPage() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [activePlayer, setActivePlayer] = useState(null);

  // Get all categories or specific category
  const categories = categoryId 
    ? { [categoryId]: MEDITATION_CATEGORIES[categoryId] }
    : MEDITATION_CATEGORIES;

  const handlePlay = (session, category) => {
    setActivePlayer({ session, category });
  };

  // If specific category
  if (categoryId && MEDITATION_CATEGORIES[categoryId]) {
    const category = MEDITATION_CATEGORIES[categoryId];
    const CategoryIcon = category.icon;

    return (
      <div className="min-h-screen bg-background pb-28">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border/50">
          <div className="px-4 pt-6 pb-4">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate(-1)}
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${category.color}20` }}
              >
                <CategoryIcon className="w-5 h-5" style={{ color: category.color }} />
              </div>
              <div>
                <h1 className="text-xl font-heading text-foreground">{category.title}</h1>
                <p className="text-white/50 text-sm">{category.subtitle}</p>
              </div>
            </div>
          </div>
        </div>

        <main className="px-4 py-6">
          <div className="space-y-4">
            {category.sessions.map(session => (
              <SessionCard 
                key={session.id}
                session={session}
                category={category}
                onPlay={handlePlay}
              />
            ))}
          </div>
        </main>

        {/* Player Modal */}
        {activePlayer && (
          <GuidedTextPlayer 
            session={activePlayer.session}
            category={activePlayer.category}
            onClose={() => setActivePlayer(null)}
          />
        )}

        <BottomNavigation />
      </div>
    );
  }

  // All meditations view
  return (
    <div className="min-h-screen bg-background pb-28">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="px-4 pt-6 pb-4">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div className="w-10 h-10 rounded-xl bg-[#a69373]/20 flex items-center justify-center">
              <Wind className="w-5 h-5 text-[#a69373]" />
            </div>
            <div>
              <h1 className="text-xl font-heading text-foreground">Meditation</h1>
              <p className="text-white/50 text-sm">All guided sessions</p>
            </div>
          </div>
        </div>
      </div>

      <main className="px-4 py-6">
        {Object.values(MEDITATION_CATEGORIES).map(category => {
          const CategoryIcon = category.icon;
          return (
            <section key={category.id} className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <div 
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${category.color}20` }}
                >
                  <CategoryIcon className="w-4 h-4" style={{ color: category.color }} />
                </div>
                <div>
                  <h2 className="font-heading text-white text-lg">{category.title}</h2>
                  <p className="text-white/50 text-xs">{category.subtitle}</p>
                </div>
              </div>
              <div className="space-y-3">
                {category.sessions.map(session => (
                  <SessionCard 
                    key={session.id}
                    session={session}
                    category={category}
                    onPlay={handlePlay}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </main>

      {/* Player Modal */}
      {activePlayer && (
        <GuidedTextPlayer 
          session={activePlayer.session}
          category={activePlayer.category}
          onClose={() => setActivePlayer(null)}
        />
      )}

      <BottomNavigation />
    </div>
  );
}
